// ApplicationCard.tsx
import { useState, useEffect, useRef } from "react"

import { ALL_STATUSES } from "./statusDefinitions"
import PersonIcon from "@assets/icons/Person.svg"
import AnimalIcon from "@assets/icons/Animal1.svg"
import { usePropertyStore } from "@features/property/propertyStore"
import {
  CardContainer,
  HeaderRow,
  TopRow,
  LeftSection,
  ColorCircle,
  AddressContainer,
  TitleSpan,
  StatusBar,
  RightSection,
  EditButton,
  ButtonGroup,
  SaveButton,
  CancelButton,
  InfoRow,
  ContactName,
  OccupantBadge,
  AnimalBadge,
  CreditCheckBadge,
  FooterRow,
  DateContainer,
  LabelText,
  Chevron,
  OuterDetailsWrapper,
  DetailsContainer,
  DetailsContent,
  EditInput,
} from "./Style"
import { CustomSelect } from "./CustomSelect"
import CaretDown from "@assets/icons/Caret-down.svg"
import CaretUp from "@assets/icons/Caret-up.svg"
import { MultiSelectDropdown } from "@components/filters/FilterDropdown/MultiSelectDropdown"
import { DESIRED_SIZE_OPTIONS } from "@pages/website/RequestApplicationForm/constants/desiredSizeOptions"
import { Application } from "@features/candidatureMgmt/applicationStore"

// ----------- UTILS -----------
function toDateInputValue(isoStr?: string | null): string {
  if (!isoStr) return ""
  const date = new Date(isoStr)
  if (isNaN(date.getTime())) return ""
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, "0")
  const d = String(date.getUTCDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

function fromDateInputValue(yyyyMMdd: string): string {
  if (!yyyyMMdd) return ""
  const [yStr, mStr, dStr] = yyyyMMdd.split("-")
  const y = parseInt(yStr, 10)
  const m = parseInt(mStr, 10) - 1
  const d = parseInt(dStr, 10)
  return new Date(Date.UTC(y, m, d, 0, 0, 0)).toISOString()
}

function formatDateFR_UTC(isoStr?: string | null): string {
  if (!isoStr) return ""
  const date = new Date(isoStr)
  if (isNaN(date.getTime())) return ""
  const day = date.getUTCDate()
  const monthIndex = date.getUTCMonth()
  const year = date.getUTCFullYear()
  const monthNamesFR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"]
  const monthName = monthNamesFR[monthIndex] || String(monthIndex + 1)
  return `${day} ${monthName} ${year}`
}

// ----------- PROPS -----------
interface ApplicationCardProps {
  data: Application
  onEdit?: (id: string, partialData: Partial<Application>) => void
  onRemove?: (id: string) => void
  onStatusChange?: (id: string, newStatus: string) => void
}

export function ApplicationCard({ data, onEdit, onRemove, onStatusChange }: ApplicationCardProps) {
  // ---------- EXTRACT ----------
  const {
    id,
    status,
    apartmentAddress,
    proposeApartment,
    moveInDate,
    desiredSize,
    numberOfOccupants,
    quebecResidenceDuration,
    comfortableWithCreditCheck,
    hasGuarantor,
    animals = [],
    visitAvailability = [],
    firstName,
    lastName,
    email,
    phone,
    additionalSpecifications,
    submittedAt,
  } = data
  const contactName = `${firstName} ${lastName}`.trim()

  const normalizedDesiredSize = Array.isArray(desiredSize) ? desiredSize : desiredSize ? [desiredSize] : []

  const normalizedVisitAvailability = Array.isArray(visitAvailability) ? visitAvailability : visitAvailability ? [visitAvailability] : []

  // ---------- LOCAL STATE (editing) ----------
  const [isEditing, setIsEditing] = useState(false)
  const [cardStatus, setCardStatus] = useState(status || "Demande reçue")

  // Basic fields
  const [editApartmentAddress, setEditApartmentAddress] = useState(apartmentAddress || "")
  const [editNumberOfOccupants, setEditNumberOfOccupants] = useState<number>(numberOfOccupants || 1)
  const [editContactName, setEditContactName] = useState(contactName)
  const [editAnimals, setEditAnimals] = useState(animals.join(", "))

  // Date fields
  const [editSubmittedDate, setEditSubmittedDate] = useState(toDateInputValue(submittedAt))
  const [editPossessionDate, setEditPossessionDate] = useState(toDateInputValue(moveInDate))

  // Additional fields
  const [editEmail, setEditEmail] = useState(email || "")
  const [editPhone, setEditPhone] = useState(phone || "")
  const [editDesiredSize, setEditDesiredSize] = useState<string[]>(desiredSize)
  const [editQuebecResidenceDuration, setEditQuebecResidenceDuration] = useState(quebecResidenceDuration || "")
  const [editVisitAvailability, setEditVisitAvailability] = useState<string[]>(visitAvailability)
  const [editAdditionalSpecifications, setEditAdditionalSpecifications] = useState(additionalSpecifications || "")

  // New local state: toggle for expanded view of additional specifications
  const [specsExpanded, setSpecsExpanded] = useState(false)

  // Expand/collapse for details section
  const [expanded, setExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(0)

  // ---------- PROPERTY STORE LOGIC ----------
  const { properties } = usePropertyStore()

  // ---------- EFFECT: Recompute Details Height ----------
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight)
    }
  }, [expanded, isEditing, editEmail, editPhone, editDesiredSize, editQuebecResidenceDuration, editVisitAvailability, editAdditionalSpecifications])

  // ---------- EFFECT: Re-sync local states when not editing ----------
  useEffect(() => {
    if (!isEditing) {
      setEditApartmentAddress(apartmentAddress || "")
      setEditNumberOfOccupants(numberOfOccupants || 1)
      setEditContactName(contactName)
      setEditAnimals(animals.join(", "))
      setEditSubmittedDate(toDateInputValue(submittedAt))
      setEditPossessionDate(toDateInputValue(moveInDate))
      setEditEmail(email || "")
      setEditPhone(phone || "")
      setEditDesiredSize(Array.isArray(desiredSize) ? desiredSize : desiredSize ? [desiredSize] : [])
      setEditQuebecResidenceDuration(quebecResidenceDuration || "")
      setEditVisitAvailability(Array.isArray(visitAvailability) ? visitAvailability : visitAvailability ? [visitAvailability] : [])
      setEditAdditionalSpecifications(additionalSpecifications || "")
      setCardStatus(status || "Demande reçue")
    }
  }, [
    apartmentAddress,
    numberOfOccupants,
    contactName,
    animals,
    submittedAt,
    moveInDate,
    email,
    phone,
    desiredSize,
    quebecResidenceDuration,
    visitAvailability,
    additionalSpecifications,
    status,
    isEditing,
  ])

  // ---------- STATUS COLOR ----------
  const statusDef = ALL_STATUSES.find((st) => st.label === cardStatus)
  const baseColor = statusDef?.color || "gray"
  const colorMap: Record<string, string> = {
    blue: "linear-gradient(135deg, #3f51b5, #5c6bc0)",
    red: "linear-gradient(135deg, #f44336, #ef5350)",
    green: "linear-gradient(135deg, #4caf50, #66bb6a)",
    yellow: "linear-gradient(135deg, #ffc107, #ffca28)",
    orange: "linear-gradient(135deg, #ffa726, #ffb74d)",
    gray: "linear-gradient(135deg, #9e9e9e, #bdbdbd)",
  }
  const finalGradient = colorMap[baseColor] || colorMap.gray

  function handleSave() {
    const [fName, ...rest] = editContactName.split(" ").map((x) => x.trim())
    const partialData: Partial<Application> = {
      status: cardStatus,
      apartmentAddress: editApartmentAddress,
      numberOfOccupants: editNumberOfOccupants,
      firstName: fName || "",
      lastName: rest.join(" "),
      animals: editAnimals
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean),
      submittedAt: fromDateInputValue(editSubmittedDate),
      moveInDate: fromDateInputValue(editPossessionDate),
      email: editEmail,
      phone: editPhone,
      desiredSize: editDesiredSize,
      quebecResidenceDuration: editQuebecResidenceDuration,
      visitAvailability: editVisitAvailability,
      additionalSpecifications: editAdditionalSpecifications,
    }
    // console.log("Saving partialData =>", partialData)

    onEdit?.(id, partialData)
    setIsEditing(false)
  }

  function handleCancel() {
    setIsEditing(false)
  }

  function toggleExpanded() {
    setExpanded((prev) => !prev)
  }

  function toggleSpecs() {
    setSpecsExpanded((prev) => !prev)
  }

  // For read-mode display
  const animalDisplay = animals.length ? animals.join(", ") : "Aucun animal"

  return (
    <>
      <CardContainer>
        <HeaderRow>
          <TopRow>
            <LeftSection>
              <ColorCircle gradient={finalGradient} />

              <AddressContainer>
                {isEditing ? (
                  // Show a dropdown of addresses when editing
                  <CustomSelect
                    options={properties.map((p) => ({ value: p.address, label: p.address }))}
                    value={editApartmentAddress}
                    onChange={(val) => setEditApartmentAddress(val)}
                  />
                ) : (
                  <TitleSpan>{editApartmentAddress}</TitleSpan>
                )}
                <StatusBar gradient={finalGradient} />
              </AddressContainer>
            </LeftSection>
            <RightSection>
              {!isEditing && (
                <CustomSelect
                  options={ALL_STATUSES.map((st) => ({ value: st.label, label: st.label }))}
                  value={cardStatus}
                  onChange={(newVal) => {
                    setCardStatus(newVal)
                    onStatusChange?.(id, newVal)
                  }}
                />
              )}
            </RightSection>
          </TopRow>
          <InfoRow margin="0.85rem 0">
            {isEditing ? (
              <>
                <EditInput value={editContactName} onChange={(e) => setEditContactName(e.target.value)} />
                <ButtonGroup>
                  <SaveButton onClick={handleSave}>Sauvegarder</SaveButton>
                  <CancelButton onClick={handleCancel}>Annuler</CancelButton>
                </ButtonGroup>
              </>
            ) : (
              <>
                <ContactName>{contactName}</ContactName>
                <EditButton onClick={() => setIsEditing(true)}>Modifier</EditButton>
              </>
            )}
          </InfoRow>
          <InfoRow>
            <OccupantBadge>
              <PersonIcon />
              {isEditing ? (
                <EditInput
                  type="number"
                  style={{ width: "60px" }}
                  value={editNumberOfOccupants}
                  onChange={(e) => setEditNumberOfOccupants(Number(e.target.value))}
                />
              ) : (
                numberOfOccupants
              )}
            </OccupantBadge>
            <AnimalBadge>
              <AnimalIcon />
              {isEditing ? (
                <EditInput value={editAnimals} onChange={(e) => setEditAnimals(e.target.value)} placeholder="chien, chat" />
              ) : (
                animalDisplay
              )}
            </AnimalBadge>
            <CreditCheckBadge ok={comfortableWithCreditCheck}>Enquête crédit ok? {comfortableWithCreditCheck ? "✔" : "✖"}</CreditCheckBadge>
          </InfoRow>
          <FooterRow>
            <DateContainer>
              <LabelText>Candidature soumise</LabelText>
              {isEditing ? (
                <EditInput type="date" value={editSubmittedDate} onChange={(e) => setEditSubmittedDate(e.target.value)} />
              ) : (
                formatDateFR_UTC(submittedAt)
              )}
            </DateContainer>
            <DateContainer>
              <LabelText>Prise de possession</LabelText>
              {isEditing ? (
                <EditInput type="date" value={editPossessionDate} onChange={(e) => setEditPossessionDate(e.target.value)} />
              ) : (
                formatDateFR_UTC(moveInDate)
              )}
            </DateContainer>
            <Chevron onClick={toggleExpanded} expanded={expanded}>
              {expanded ? <CaretUp /> : <CaretDown />}
            </Chevron>
          </FooterRow>
        </HeaderRow>
      </CardContainer>

      {/* Expandable Details */}
      <OuterDetailsWrapper expanded={expanded}>
        <DetailsContainer expanded={expanded} maxHeight={contentHeight}>
          <DetailsContent ref={contentRef}>
            {isEditing ? (
              <>
                <div>
                  <strong>Email :</strong> <EditInput value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                </div>
                <div>
                  <strong>Téléphone :</strong> <EditInput value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
                </div>
                <div>
                  <strong>Grandeur désirée :</strong>
                  <MultiSelectDropdown
                    options={DESIRED_SIZE_OPTIONS.map((option) => ({ value: option, label: option }))}
                    selected={editDesiredSize}
                    onChange={(selected) => setEditDesiredSize(selected)}
                    placeholder="Sélectionnez une ou plusieurs options"
                  />
                </div>
                <div>
                  <strong>Québec depuis :</strong>{" "}
                  <CustomSelect
                    options={[
                      { value: "plus d'un an", label: "Plus d'un an" },
                      { value: "moins d'un an", label: "Moins d'un an" },
                    ]}
                    value={editQuebecResidenceDuration}
                    onChange={(newVal) => {
                      if (newVal === "plus d'un an" || newVal === "moins d'un an") {
                        setEditQuebecResidenceDuration(newVal)
                      }
                    }}
                    placeholder="Sélectionnez..."
                  />
                </div>
                <div>
                  <strong>Disponibilité pour visite :</strong>{" "}
                  <MultiSelectDropdown
                    options={[
                      { value: "Soir de semaine", label: "Soir de semaine" },
                      { value: "fin de semaine en am", label: "Fin de semaine en am" },
                      { value: "fin de semaine en pm", label: "Fin de semaine en pm" },
                    ]}
                    selected={editVisitAvailability} // now an array
                    onChange={(selected) => setEditVisitAvailability(selected)}
                    placeholder="Sélectionnez..."
                  />
                </div>
                <div>
                  <strong>Spécifications supplémentaires :</strong>{" "}
                  {specsExpanded || isEditing ? (
                    // In edit mode, always show the input expanded
                    <EditInput
                      value={editAdditionalSpecifications}
                      onChange={(e) => setEditAdditionalSpecifications(e.target.value)}
                      placeholder="Entrez les spécifications supplémentaires..."
                    />
                  ) : (
                    <div onClick={toggleSpecs} style={{ cursor: "pointer", color: "blue" }}>
                      Voir plus...
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div>
                  <strong>Email :</strong> {email}
                </div>
                <div>
                  <strong>Téléphone :</strong> {phone}
                </div>
                <div>
                  <strong>Grandeur désirée :</strong> {normalizedDesiredSize.length > 0 ? normalizedDesiredSize.join(", ") : "Aucune"}
                </div>
                <div>
                  <strong>Québec depuis :</strong> {quebecResidenceDuration}
                </div>
                <div>
                  <strong>Garant :</strong> {hasGuarantor ? "Oui" : "Non"}
                </div>
                <div>
                  <strong>Disponibilité pour visite :</strong>{" "}
                  {normalizedVisitAvailability.length > 0 ? normalizedVisitAvailability.join(", ") : "Aucune"}
                </div>
                <div>
                  <strong>Spécifications supplémentaires :</strong>{" "}
                  {additionalSpecifications ? (
                    <div onClick={toggleSpecs} style={{ cursor: "pointer", color: "blue" }}>
                      {specsExpanded ? additionalSpecifications : "Voir plus..."}
                    </div>
                  ) : (
                    "Aucune"
                  )}
                </div>
              </>
            )}
          </DetailsContent>
        </DetailsContainer>
      </OuterDetailsWrapper>
    </>
  )
}
